import { encode } from 'html-entities';
import { UNAVAILABLE_ITEMS_ERROR_CODE } from '@/constants/basket';
import { lookupErrorSlugCopy } from '@/constants/errorSlugCopy';
import {
  GENERIC_ERROR_COPY,
  lookupFpoErrorCopy,
  type WireFpoErrorCode,
} from '@/constants/fpoErrorCopy';
import {
  lookupPromoRejectionCopy,
  PROMO_OUTSIDE_SCHEDULE_FALLBACK_COPY,
  PROMO_OUTSIDE_SCHEDULE_WIRE_CODE,
  PROMO_REJECTION_FALLBACK_COPY,
  PROMO_REJECTION_OUTSIDE_SCHEDULE_REASON,
} from '@/constants/promoRejectionCopy';
import { type FpoErrorEntry, type IErrorResponse } from '@/types/errors';
import { getErrorDataObject, getFpoErrorEntries } from '@/utils/errorEnvelope';
import { formatPromoScheduleMessage } from '@/utils/promoSchedule';
import { sentryLogger } from '@/utils/sentryLogger';

/**
 * TEAM-13882: resolve diner-facing copy from the numeric Chowly FPO error code, never from
 * `error_description`. The render rule itself is documented on `resolveDinerMessage` below.
 *
 * @see resolveDinerMessage
 * @see docs/DINER_ERROR_MESSAGES.md
 */

const NUMERIC_CODE = /^\d+$/;

/**
 * Deliberately not `parseInt` — `parseInt('32abc')` is `32`, which would map junk onto real copy.
 * Codes stay numeric strings all the way through, exactly as they arrive.
 *
 * The result is a `WireFpoErrorCode`, **not** an `FpoErrorCode`: all this function establishes is
 * that the value is a numeric string, and coo-api can send a numeric code the catalog has never
 * heard of. Typing it as a catalog key would make `FPO_ERROR_COPY[code]` look total when it is not.
 * `lookupFpoErrorCopy` is how a wire code becomes copy.
 */
function asWireFpoErrorCode(value: unknown): WireFpoErrorCode | null {
  if (typeof value !== 'string' && typeof value !== 'number') {
    return null;
  }
  const candidate = String(value);
  return NUMERIC_CODE.test(candidate) ? (candidate as WireFpoErrorCode) : null;
}

/**
 * True when this failure already drives a dedicated UI that names the individual items, so resolving
 * item-specific copy here would double-message the diner.
 *
 * A `BasketNotValidException` can carry **both** an FPO error array and the reshaped
 * `unavailable_items` (coo-api `Handler::convertKoalaException`), so this guard is load-bearing
 * rather than belt-and-braces. The inline notice at
 * `features/checkout/summary/items/items-unavailable-notice.tsx` owns that copy.
 */
function hasDedicatedUnavailableItemsUi(error: IErrorResponse | null): boolean {
  if (!error) {
    return false;
  }
  if ((getErrorDataObject(error)?.unavailable_items?.length ?? 0) > 0) {
    return true;
  }
  return (
    error.error === UNAVAILABLE_ITEMS_ERROR_CODE.ITEMS_OUT_OF_HOURS ||
    error.error === UNAVAILABLE_ITEMS_ERROR_CODE.ITEMS_UNAVAILABLE
  );
}

/**
 * The primary FPO error is the **first** one with a numeric code, not the "most severe". The entries
 * carry no severity field, so any ranking would be invented here with no backend counterpart and
 * would drift the moment coo-api reorders them.
 *
 * Pure by design: the "more than one code" warning lives in `extractPrimaryFpoCode` so that
 * resolving one error can never emit it twice. This used to log here, and `resolveDinerMessage`
 * reaches the primary entry along two paths (the code, and tier 3's vetted copy), so a
 * multiple-code envelope warned once per path.
 */
function getPrimaryFpoError(error: IErrorResponse | null): FpoErrorEntry | null {
  return getCodedFpoErrors(error)[0] ?? null;
}

function getCodedFpoErrors(error: IErrorResponse | null): FpoErrorEntry[] {
  return getFpoErrorEntries(error).filter(
    (entry) => entry && typeof entry === 'object' && asWireFpoErrorCode(entry.code) !== null,
  );
}

/**
 * An FPO entry's `additional_data` as an object, or `null` when it is absent, not JSON, or not a
 * plain object.
 *
 * coo-api `json_encode`s `additional_data` (see `FpoErrorEntry`), so it almost always arrives as a
 * JSON **string** (`'{"reason":"expired"}'`, or `'[]'` when empty); an object is admitted too so a
 * future non-encoded path parses without a cast.
 */
function parseAdditionalData(
  additionalData: FpoErrorEntry['additional_data'],
): Record<string, unknown> | null {
  if (!additionalData) {
    return null;
  }
  let parsed: unknown = additionalData;
  if (typeof additionalData === 'string') {
    try {
      parsed = JSON.parse(additionalData) as unknown;
    } catch {
      return null;
    }
  }
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
    return null;
  }
  return parsed as Record<string, unknown>;
}

interface PromoRejection {
  reason: string;
  additionalData: Record<string, unknown>;
}

/**
 * The first FPO entry carrying a promo-rejection `reason` in its `additional_data` — TEAM-14743 —
 * together with the rest of that `additional_data` (TEAM-15229 reads the schedule from it). Anything
 * that is not a non-empty string `reason` is not a classification and must reach the generic path.
 * Only chowly-api's typed discount rejections put a `reason` in `additional_data` today, so the first
 * entry carrying one is the promo rejection.
 */
function getPromoRejection(error: IErrorResponse | null): PromoRejection | null {
  for (const entry of getFpoErrorEntries(error)) {
    const additionalData = parseAdditionalData(entry?.additional_data);
    const reason = additionalData?.reason;
    if (additionalData && typeof reason === 'string' && reason.trim() !== '') {
      return { reason, additionalData };
    }
  }
  return null;
}

/**
 * Tier 0's sentence for a classified promo rejection.
 *
 * `outside_schedule` (TEAM-15229) is the one reason whose copy is built rather than looked up: it
 * names the days and hours the code works, from the schedule in the same `additional_data`, and
 * falls back to its own fixed line when that schedule cannot be turned into a sentence.
 */
function resolvePromoRejectionCopy({ reason, additionalData }: PromoRejection): string {
  if (reason === PROMO_REJECTION_OUTSIDE_SCHEDULE_REASON) {
    return (
      formatPromoScheduleMessage(
        additionalData.recurring_days,
        additionalData.recurring_time_windows,
      ) ?? PROMO_OUTSIDE_SCHEDULE_FALLBACK_COPY
    );
  }
  return lookupPromoRejectionCopy(reason) ?? PROMO_REJECTION_FALLBACK_COPY;
}

export function extractPrimaryFpoCode(error: IErrorResponse | null): WireFpoErrorCode | null {
  if (!error || hasDedicatedUnavailableItemsUi(error)) {
    return null;
  }

  const coded = getCodedFpoErrors(error);
  if (coded.length > 1) {
    // When there is more than one code we log rather than rank, so a real ordering requirement shows
    // up as data before it shows up as a guess.
    sentryLogger.warn('Diner error copy: multiple FPO error codes in one envelope', {
      eventName: 'DINER_ERROR_COPY_MULTIPLE_CODES',
      codes: coded.map((entry) => String(entry.code)),
      errorSlug: error.error,
    });
  }

  // The FPO error array (BasketNotValidException / UnableToSetTipAmountException).
  const primaryCode = asWireFpoErrorCode(coded[0]?.code);
  if (primaryCode) {
    return primaryCode;
  }

  // The flat `{ code }` shape — coo-api's restaurant-temporarily-unavailable branch. Deliberately
  // narrow: `failures`, `unavailable_items` and `basket_id` are never treated as a code.
  const flatCode = asWireFpoErrorCode(getErrorDataObject(error)?.code);
  if (flatCode) {
    return flatCode;
  }

  // Forward-compatible only. No deployed coo-api version sends a top-level `error_code` yet; the
  // companion backend change is the backend half of TEAM-13882 (see
  // docs/DINER_ERROR_MESSAGES.md#where-this-is-incomplete). Last on purpose, so the untested branch
  // can never pre-empt one we have evidence for.
  return asWireFpoErrorCode(error.error_code);
}

/**
 * Tier 3. Upstream marks these strings as intended for diners, not as safe HTML. Escape both
 * sources because the global toast inserts `message` as HTML; preserve the raw envelope for
 * classification and reporting.
 *
 * Two halves, and which one applies matters: the FPO entry's `detail`, and the **envelope-level**
 * `user_message`. The caller clears `primary` when a dedicated UI owns item-specific copy, which
 * disables the first half only — the envelope-level `user_message` remains eligible in that case.
 *
 * The envelope-level half is where coo-api's `option_not_on_menu` sentence resolves (TEAM-14755 /
 * coo-api#358), because `@/constants/errorSlugCopy` catalogues that slug as `null` so tier 2 defers
 * to it rather than shadowing it with a second sentence owned by this repo.
 */
function getVettedUpstreamCopy(
  error: IErrorResponse | null,
  primary: FpoErrorEntry | null,
): string | null {
  if (primary?.user_friendly && primary.detail) {
    return encode(primary.detail);
  }
  return error?.user_message ? encode(error.user_message) : null;
}

export interface ResolveDinerMessageArgs {
  error: IErrorResponse | null;
  /** The call site's own copy. Tier 4 — a better fallback than one global sentence. */
  staticMessage?: string | null;
}

/**
 * Resolve the sentence a diner reads from a coo-api error envelope. **The canonical statement of the
 * render rule** — every other reference to it in the codebase points here.
 *
 * Applied exactly once per error, by `prepareErrorMessage` (`@/utils/global`). In order:
 *
 * 1. `lookupFpoErrorCopy(code)` — product-owned copy keyed by the numeric code
 * 2. else `lookupErrorSlugCopy(error.error)` — product-owned copy keyed by a **semantic** `error`
 *    slug, for coo-api's own validations, which carry no numeric code at all. A slug catalogued as
 *    `null` means the catalog is deferring on purpose and resolution continues below
 * 3. else the primary FPO error's `detail` when its `user_friendly` flag is set, else the envelope's
 *    `user_message` — both HTML-escaped before display
 * 4. else the call site's own static message
 * 5. else `GENERIC_ERROR_COPY`
 * 6. **never** `error_description`, and never the bare `error` slug
 *
 * Tier 3 sits *below* tiers 1 and 2 on purpose: `user_friendly: true` is not a reliable diner-safe
 * filter (chowly-api flags both `Dependency` and `MalformedPayload` as user-friendly and both read
 * like log lines). Because all 37 codes are catalogued, tier 1 always wins for them.
 *
 * Tier 2 is the slug catalog, not the bare slug: the slug selects a product-authored sentence and is
 * never itself displayed, so tier 6 still holds. It sits below tier 1 because a numeric code is the
 * more specific signal whenever both are present, and above tier 3 for the same reason tier 1 is —
 * authored copy beats upstream text that merely claims to be diner-safe. Only *semantic* slugs are
 * eligible; see `@/constants/errorSlugCopy` for why an HTTP status name never is.
 *
 * **Outranking tier 3 is exactly why a catalogued `null` matters here.** Tier 2 winning is only
 * right when this repo has better copy than the backend sent. When coo-api has authored a sentence
 * for the failure — as it does for `option_not_on_menu` (coo-api#358), whose envelope carries a
 * populated `user_message` — a string in the slug catalog would shadow it and leave the backend's
 * copy dead with nothing reporting the fact. So that slug is catalogued `null`: tier 2 declines and
 * tier 3 renders coo-api's sentence, HTML-escaped. Today that is the slug catalog's only entry, so
 * tier 2 currently resolves nothing at runtime — the tier exists for the envelope that carries
 * neither a code nor a `user_message`, which is the case the catalog was built for.
 *
 * A slug-catalogued `null` is therefore **not** the same as an FPO-catalogued `null`: the FPO one
 * suppresses lower tiers (a dedicated UI is already speaking), this one hands off to them.
 *
 * Tiers 1, 2 and the FPO half of tier 3 are suppressed when the failure already drives a dedicated
 * UI that names the affected items — see `hasDedicatedUnavailableItemsUi` and
 * `getVettedUpstreamCopy`.
 *
 * This closes the leak by construction: anything unmapped is safe rather than leaked.
 *
 * @see docs/DINER_ERROR_MESSAGES.md
 */
export function resolveDinerMessage({ error, staticMessage }: ResolveDinerMessageArgs): string {
  /**
   * Tier 0 (TEAM-14743): a promo-code rejection carries a Product-approved sentence keyed by
   * `additional_data.reason`. Above the numeric-code tier by design — the reason is the specific
   * signal, and these arrive under chowly-api codes 39-45 that `FPO_ERROR_COPY` deliberately does
   * not catalogue, so returning here both selects the right copy and keeps the drift detector quiet
   * for a rejection we classify by reason instead of code. A reason present but not one of the seven
   * (e.g. `usage_limit_reached`, which chowly-api emits with no Product copy yet) resolves to the
   * never-blank fallback rather than falling through to the generic upstream text. A rejection with
   * no reason (chowly-api's own unclassifiable code 23) is not caught here and resolves at tier 1.
   * `outside_schedule` (TEAM-15229) builds its sentence from the schedule in the same
   * `additional_data` — see `resolvePromoRejectionCopy`.
   */
  const promoRejection = getPromoRejection(error);
  if (promoRejection) {
    return resolvePromoRejectionCopy(promoRejection);
  }

  // `extractPrimaryFpoCode` applies the same bail-out itself, so tier 1 needs no extra guard here;
  // `primary` is nulled separately because tier 3 reads the entry rather than its code, and the slug
  // tier below reads it for the same reason.
  const hasDedicatedUi = hasDedicatedUnavailableItemsUi(error);
  const primary = hasDedicatedUi ? null : getPrimaryFpoError(error);
  const code = extractPrimaryFpoCode(error);

  /**
   * A catalogued `null` is the deliberate "a dedicated UI already tells the diner what happened".
   * That silence has to bind tier 2 as well, or the invariant becomes conditional on the backend
   * never sending a `null`-catalogued code alongside a catalogued slug — true today (only codes `13`
   * and `32` are `null`, and neither co-occurs with one) but not something this function should depend on.
   */
  let suppressedByCatalog = false;

  if (code) {
    /**
     * TEAM-15229 AC6: an `outside_schedule` rejection whose `additional_data` could not carry the
     * reason (missing, not JSON, no `reason`) never reached tier 0, but its wire code still says what
     * it is. Answered here, before the catalog lookup, so code 46 — deliberately absent from
     * `FPO_ERROR_COPY` — neither renders the generic upstream `detail` nor trips the drift detector.
     */
    if (code === PROMO_OUTSIDE_SCHEDULE_WIRE_CODE) {
      return PROMO_OUTSIDE_SCHEDULE_FALLBACK_COPY;
    }
    const copy = lookupFpoErrorCopy(code);
    if (copy) {
      return copy;
    }
    if (copy === null) {
      suppressedByCatalog = true;
    }
    // `undefined` — not in the catalog at all — is the drift signal. A catalogued `null` is the
    // deliberate "a dedicated UI already says this" and must stay silent, so the three-way return of
    // `lookupFpoErrorCopy` is load-bearing rather than incidental.
    if (copy === undefined) {
      // A code coo-api sent that this catalog does not know: the drift detector. Scoped narrowly on
      // purpose — warning whenever *no* code was found would fire on the majority of errors
      // (`error_data: []`, `{failures}`, `{basket_id}`) and bury this signal.
      sentryLogger.warn('Diner error copy: no catalog entry for FPO error code', {
        eventName: 'DINER_ERROR_COPY_CATALOG_MISS',
        fpoErrorCode: code,
        errorSlug: error?.error,
      });
    }
  }

  /**
   * Tier 2. Deliberately **outside** the `if (code)` block above, for two reasons.
   *
   * Placement: a numeric code is the more specific signal, so when a code resolves to copy the block
   * above has already returned and this never runs. Reaching here means no code resolved.
   *
   * No drift warning: `DINER_ERROR_COPY_CATALOG_MISS` fires only inside that block, on a
   * `lookupFpoErrorCopy` miss. Routing the slug through `extractPrimaryFpoCode` or
   * `lookupFpoErrorCopy` to reuse its plumbing is the one change that would make the warning start
   * firing here — and it would then fire on `bad_request` / `not_found` / `access_denied` platform
   * wide, burying the FPO signal it exists to give. An unmapped slug falls through silently on
   * purpose.
   */
  if (!hasDedicatedUi && !suppressedByCatalog) {
    const slugCopy = lookupErrorSlugCopy(error?.error);
    // Truthiness, so a catalogued `null` falls through to tier 3 rather than returning. That is not
    // incidental: it is how `option_not_on_menu` reaches coo-api's own `user_message` instead of
    // being shadowed by a sentence this repo would then own in parallel. Unlike the FPO catalog's
    // `null`, this one must **not** set `suppressedByCatalog`.
    if (slugCopy) {
      return slugCopy;
    }
  }

  // Truthiness, not `??`, on both operands: `redux/conveyanceMode/saga.ts` passes `''` for its
  // non-curbside branch and coo-api can send an empty `user_message`, either of which would render
  // an empty toast under `??`.
  // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
  return getVettedUpstreamCopy(error, primary) || staticMessage || GENERIC_ERROR_COPY;
}
